import React, { useEffect, useMemo, useState } from 'react';
import QueryString from 'query-string';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SettingsAccessibilityIcon from '@mui/icons-material/SettingsAccessibility';
import TuneIcon from '@mui/icons-material/Tune';
import Loading from '../../components/shared/Loading';
import Page from '../Page';
import ServerError from '../error/ServerError';
import config from '../../config';

const MY_CREATED_WORKOUTS = gql`
  query MyCreatedWorkouts($input: MyCreatedWorkoutsQueryInput) {
    myCreatedWorkouts(input: $input) {
      _id
      title
      desc
      status
      coverImageUrl
      createdAt
      updatedAt
      durationTotal
      exerciseCount
      completedCount
      likedCount
      tags
      workoutType
      level
      equipment {
        name
        imageUrl
      }
      exercises {
        _id
      }
      exerciseGroups {
        _id
        groupName
        exercises {
          _id
        }
      }
      trainingPlans {
        _id
        title
      }
    }
  }
`;

const CREATE_WORKOUT = gql`
  mutation CreateWorkout($input: WorkoutInput!) {
    createWorkout(input: $input) {
      _id
    }
  }
`;

const LIMIT = 50;

const WORKOUT_TYPE_LABELS = {
  TIMED: 'Timed',
  REP_BASED: 'Rep Based',
  VIDEO: 'Video',
  YOUTUBE: 'YouTube',
  INSTAGRAM: 'Instagram',
};

const categoryColor = (category) => {
  if (
    category === 'Video' ||
    category === 'YouTube' ||
    category === 'Instagram'
  ) {
    return { color: '#6d28d9', bg: '#f0e7ff' };
  }
  if (category === 'Rep Based') return { color: '#ea580c', bg: '#fff3e3' };
  if (category === 'Timed') return { color: '#118847', bg: '#dcf7e7' };
  return { color: '#40536b', bg: '#eef2f7' };
};

const exerciseCount = (workout) => {
  const groupCount = (workout.exerciseGroups ?? []).reduce(
    (total, group) => total + (group.exercises?.length ?? 0),
    0
  );
  return groupCount || workout.exercises?.length || 0;
};

const durationMinutes = (workout) =>
  workout.durationTotal
    ? Math.max(1, Math.floor(workout.durationTotal / 60))
    : 0;

const tagsFor = (workout) => (workout.tags || []).slice(0, 3);

const workoutCategory = (workout) =>
  WORKOUT_TYPE_LABELS[workout.workoutType] || 'Uncategorized';

const levelLabel = (level) =>
  ['Beginner', 'Intermediate', 'Advanced', 'Advanced'][level] || 'All Levels';

const durationMatches = (workout, filter) => {
  const minutes = durationMinutes(workout);
  if (filter === 'ALL') return true;
  if (filter === 'LT_10') return minutes > 0 && minutes < 10;
  if (filter === '10_20') return minutes >= 10 && minutes < 20;
  if (filter === '20_30') return minutes >= 20 && minutes < 30;
  if (filter === 'GT_30') return minutes > 30;
  if (filter === '45') return minutes >= 45 && minutes < 60;
  if (filter === '60') return minutes >= 60;
  return true;
};

const NoCover = ({ height = '100%' }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      color: '#64748b',
      display: 'flex',
      fontSize: 13,
      fontWeight: 700,
      height,
      justifyContent: 'center',
      width: '100%',
    }}
  >
    No cover image
  </Box>
);

const StatCard = ({ icon, value, label, color, backgroundColor }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ py: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            color,
            backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 25, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const RecentlyEditedCard = ({ workout }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{ width: 52, height: 42, borderRadius: 1, overflow: 'hidden' }}
        >
          {workout.coverImageUrl ? (
            <Box
              component="img"
              src={workout.coverImageUrl}
              alt={workout.title}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <NoCover />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 800, fontSize: 14 }}>
            {workout.title}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 13 }}>
            {workout.updatedAt
              ? new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }).format(new Date(workout.updatedAt))
              : 'Not updated'}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const WorkoutCard = ({ workout }) => {
  const navigate = useNavigate();
  const category = workoutCategory(workout);
  const colors = categoryColor(category);
  const count = workout.exerciseCount ?? exerciseCount(workout);
  const duration = durationMinutes(workout);
  const usedIn = workout.trainingPlans?.length ?? 0;
  const completedCount = workout.completedCount ?? 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#e2e8f0',
        borderRadius: 1,
        overflow: 'hidden',
        height: '100%',
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Box
        sx={{ position: 'relative', height: 145, backgroundColor: '#111827' }}
      >
        {workout.coverImageUrl ? (
          <Box
            component="img"
            src={workout.coverImageUrl}
            alt={workout.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <NoCover />
        )}
        <Chip
          size="small"
          label={category}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            color: colors.color,
            backgroundColor: colors.bg,
            fontWeight: 800,
          }}
        />
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: 18, mb: 1.25 }}>
          {workout.title}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mb: 1.25 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AccessTimeIcon sx={{ color: '#40536b', fontSize: 17 }} />
            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
              {duration ? `${duration} min` : '-'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TuneIcon sx={{ color: '#40536b', fontSize: 17 }} />
            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
              {count} exercises
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.75 }}
        >
          {tagsFor(workout).map((tag) => (
            <Chip
              key={tag}
              size="small"
              label={tag}
              sx={{ height: 22, fontWeight: 700 }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: '#40536b', fontSize: 17 }} />
            <Typography sx={{ color: '#40536b', fontSize: 13 }}>
              Used in {usedIn} plans
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: '#40536b', fontSize: 17 }} />
            <Typography sx={{ color: '#40536b', fontSize: 13 }}>
              Completed {completedCount} times
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.25}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/trainer/workouts/${workout._id}`)}
          >
            Edit
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/trainer/workouts/${workout._id}/preview`)}
          >
            Preview
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function WorkoutList() {
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [equipmentFilter, setEquipmentFilter] = useState('ALL');
  const [durationFilter, setDurationFilter] = useState('ALL');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, data, refetch, fetchMore } = useQuery(
    MY_CREATED_WORKOUTS,
    {
      variables: { input: { trainerId, query: searchQuery, limit: LIMIT } },
    }
  );
  const [createWorkout] = useMutation(CREATE_WORKOUT);

  const workouts = data?.myCreatedWorkouts ?? [];
  const equipmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          workouts
            .flatMap((workout) => workout.equipment || [])
            .map((equipment) => equipment?.name)
            .filter(Boolean)
        )
      ).sort(),
    [workouts]
  );
  const visibleWorkouts = useMemo(
    () =>
      workouts.filter((workout) => {
        const matchesSearch = (workout.title ?? '')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const matchesCategory =
          categoryFilter === 'ALL' || workout.workoutType === categoryFilter;
        const matchesEquipment =
          equipmentFilter === 'ALL' ||
          (workout.equipment || []).some(
            (equipment) => equipment?.name === equipmentFilter
          );
        const matchesDuration = durationMatches(workout, durationFilter);
        const matchesLevel =
          levelFilter === 'ALL' || `${workout.level}` === levelFilter;

        return (
          matchesSearch &&
          matchesCategory &&
          matchesEquipment &&
          matchesDuration &&
          matchesLevel
        );
      }),
    [
      categoryFilter,
      durationFilter,
      equipmentFilter,
      levelFilter,
      searchQuery,
      workouts,
    ]
  );
  const totalPages = Math.max(1, Math.ceil(visibleWorkouts.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart =
    visibleWorkouts.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const pageEnd = Math.min(currentPage * perPage, visibleWorkouts.length);
  const paginatedWorkouts = visibleWorkouts.slice(pageStart - 1, pageEnd);

  const handleCreate = (event) => {
    event.preventDefault();
    createWorkout({
      variables: { input: { title: 'Untitled' } },
    }).then((r) => {
      if (r.data?.createWorkout?._id) {
        navigate(`/trainer/workouts/${r.data.createWorkout._id}`);
      }
    });
  };

  const handleLoadMore = async (event) => {
    event.preventDefault();
    const lastId = workouts[workouts.length - 1]._id;
    await fetchMore({
      variables: {
        input: {
          trainerId,
          query: searchQuery,
          limit: LIMIT,
          lastId,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => ({
        myCreatedWorkouts: [
          ...previousResult.myCreatedWorkouts,
          ...fetchMoreResult.myCreatedWorkouts,
        ],
      }),
    });
  };

  useEffect(() => {
    refetch();
  }, [refetch, location]);

  useEffect(() => {
    setPage(1);
  }, [
    categoryFilter,
    durationFilter,
    equipmentFilter,
    levelFilter,
    perPage,
    searchQuery,
  ]);

  if (error) return <ServerError message={error.message ?? 'Failed to load'} />;
  if (loading || !data?.myCreatedWorkouts) return <Loading />;

  const avgDuration =
    workouts.length === 0
      ? 0
      : Math.round(
          workouts.reduce((sum, workout) => sum + durationMinutes(workout), 0) /
            workouts.length
        );
  const avgExercises =
    workouts.length === 0
      ? 0
      : Math.round(
          workouts.reduce((sum, workout) => sum + exerciseCount(workout), 0) /
            workouts.length
        );
  const usedInPlans = workouts.reduce(
    (sum, workout) => sum + (workout.trainingPlans?.length ?? 0),
    0
  );
  const completedCount = workouts.reduce(
    (sum, workout) => sum + (workout.completedCount ?? 0),
    0
  );
  const categoryCounts = workouts.reduce((counts, workout) => {
    const category = workoutCategory(workout);
    counts[category] = (counts[category] || 0) + 1;
    return counts;
  }, {});
  const topTags = Object.entries(
    workouts
      .flatMap((workout) => workout.tags || [])
      .reduce((counts, tag) => {
        counts[tag] = (counts[tag] || 0) + 1;
        return counts;
      }, {})
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <Page title={`Workouts : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 30, fontWeight: 800, color: '#071120', mb: 1 }}
            >
              Workouts
            </Typography>
            <Typography sx={{ color: '#40536b', fontSize: 16 }}>
              Create, manage and organize your workouts.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="#"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              backgroundColor: '#4338ca',
              px: 2.5,
              py: 1.25,
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Create New Workout
          </Button>
        </Stack>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<FitnessCenterIcon />}
              value={workouts.length}
              label="Total Workouts"
              color="#4338ca"
              backgroundColor="#edeaff"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<AccountTreeOutlinedIcon />}
              value={usedInPlans}
              label="Used in Plans"
              color="#16a34a"
              backgroundColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<SettingsAccessibilityIcon />}
              value={completedCount}
              label="Completed Workouts"
              color="#2f80ed"
              backgroundColor="#e5f1ff"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<AccessTimeIcon />}
              value={`${avgDuration} min`}
              label="Avg. Duration"
              color="#f97316"
              backgroundColor="#fff0df"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<TuneIcon />}
              value={avgExercises}
              label="Avg. Exercises"
              color="#8b5cf6"
              backgroundColor="#f0e7ff"
            />
          </Grid>
        </Grid>

        <Card
          variant="outlined"
          sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}
        >
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search workouts..."
                sx={{ minWidth: { xs: '100%', lg: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#40536b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Select
                size="small"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                sx={{ minWidth: 145 }}
              >
                <MenuItem value="ALL">Type: All</MenuItem>
                {Object.entries(WORKOUT_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    Type: {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={equipmentFilter}
                onChange={(event) => setEquipmentFilter(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="ALL">Equipment: All</MenuItem>
                {equipmentOptions.map((equipment) => (
                  <MenuItem key={equipment} value={equipment}>
                    Equipment: {equipment}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={durationFilter}
                onChange={(event) => setDurationFilter(event.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="ALL">Duration: Any</MenuItem>
                <MenuItem value="LT_10">Less than 10 min</MenuItem>
                <MenuItem value="10_20">10-20 min</MenuItem>
                <MenuItem value="20_30">20-30 min</MenuItem>
                <MenuItem value="GT_30">More than 30 min</MenuItem>
                <MenuItem value="45">45 min</MenuItem>
                <MenuItem value="60">1 hr+</MenuItem>
              </Select>
              <Select
                size="small"
                value={levelFilter}
                onChange={(event) => setLevelFilter(event.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="ALL">Level: All</MenuItem>
                {[0, 1, 2, 3].map((level) => (
                  <MenuItem key={level} value={`${level}`}>
                    Level: {levelLabel(level)}
                  </MenuItem>
                ))}
              </Select>
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('ALL');
                  setEquipmentFilter('ALL');
                  setDurationFilter('ALL');
                  setLevelFilter('ALL');
                }}
              >
                Reset
              </Button>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={9.6}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
                    Recently Edited
                  </Typography>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {visibleWorkouts.slice(0, 4).map((workout) => (
                    <Grid item xs={12} sm={6} xl={3} key={workout._id}>
                      <RecentlyEditedCard workout={workout} />
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={3}>
                  {paginatedWorkouts.map((workout) => (
                    <Grid item xs={12} sm={6} xl={3} key={workout._id}>
                      <WorkoutCard workout={workout} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} lg={2.4}>
                <Stack spacing={2}>
                  <Card
                    variant="outlined"
                    sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}
                  >
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 2 }}>
                        Categories
                      </Typography>
                      {[
                        ['Strength', categoryCounts.Strength || 0],
                        ['Conditioning', categoryCounts.Conditioning || 0],
                        ['Mobility', categoryCounts.Mobility || 0],
                        ['Recovery', categoryCounts.Recovery || 0],
                      ].map(([label, count]) => (
                        <Stack
                          key={label}
                          direction="row"
                          justifyContent="space-between"
                          sx={{ py: 0.75 }}
                        >
                          <Typography sx={{ color: '#0f172a', fontSize: 14 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ color: '#40536b', fontSize: 14 }}>
                            {count}
                          </Typography>
                        </Stack>
                      ))}
                    </CardContent>
                  </Card>

                  <Card
                    variant="outlined"
                    sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}
                  >
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 2 }}>
                        Top Tags
                      </Typography>
                      {(topTags.length ? topTags : [['No tags', 0]]).map(
                        ([label, count]) => {
                          const value =
                            workouts.length === 0
                              ? 0
                              : Math.round((count / workouts.length) * 100);
                          return (
                            <Box key={label} sx={{ mb: 1.25 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                              >
                                <Typography sx={{ fontSize: 14 }}>
                                  {label}
                                </Typography>
                                <Typography sx={{ fontSize: 14 }}>
                                  {value}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={value}
                                sx={{ height: 4, borderRadius: 999 }}
                              />
                            </Box>
                          );
                        }
                      )}
                    </CardContent>
                  </Card>

                  <Card
                    variant="outlined"
                    sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}
                  >
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 2 }}>
                        Quick Actions
                      </Typography>
                      {[['Create New Workout', handleCreate]].map(([label, onClick]) => (
                        <Button
                          key={label}
                          fullWidth
                          onClick={onClick}
                          startIcon={<AddIcon />}
                          sx={{ justifyContent: 'flex-start', mb: 1 }}
                        >
                          {label}
                        </Button>
                      ))}
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 3 }}
        >
          <Typography sx={{ color: '#40536b' }}>
            Showing {pageStart} to {pageEnd} of {visibleWorkouts.length}{' '}
            workouts
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              sx={{ minWidth: 34 }}
            >
              ←
            </Button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <Button
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  variant={pageNumber === currentPage ? 'contained' : 'text'}
                  sx={{ minWidth: 34 }}
                >
                  {pageNumber}
                </Button>
              )
            )}
            <Button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              sx={{ minWidth: 34 }}
            >
              →
            </Button>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Select
              size="small"
              value={perPage}
              onChange={(event) => setPerPage(Number(event.target.value))}
            >
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
            </Select>
            <Typography sx={{ color: '#40536b' }}>per page</Typography>
          </Stack>
        </Stack>

        {workouts.length > 1 && workouts.length >= LIMIT && (
          <Box mb={3}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Load More
            </Button>
          </Box>
        )}
      </Container>
    </Page>
  );
}
